import { useEffect, useId, useRef, useState } from "react";
import { searchAdminCustomers } from "@/api/admin.api.js";
import { customerDisplayName } from "@/utils/customerProfile.js";
import { useViewportAnchoredMenu } from "@/hooks/useViewportAnchoredMenu.js";

const SEARCH_DEBOUNCE_MS = 2000;
const MIN_SEARCH_LENGTH = 2;

function formatCustomerOption(customer) {
    const displayName = customerDisplayName(customer);
    if (displayName && customer.username) {
        return `${displayName} (${customer.username})`;
    }
    return customer.username || displayName;
}

export default function CustomerFilterField({ value, onChange, onCustomerSelect, disabled = false }) {
    const listboxId = useId();
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const suggestionsRef = useRef(null);
    const [query, setQuery] = useState(value || "");
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchError, setSearchError] = useState("");
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const inputValue = selectedCustomer ? formatCustomerOption(selectedCustomer) : query;

    useViewportAnchoredMenu(showSuggestions && suggestions.length > 0, inputRef, suggestionsRef, {
        preferAlign: "start",
        matchAnchorWidth: true
    });

    useEffect(() => {
        if (!value) {
            setSelectedCustomer(null);
            setQuery("");
            return;
        }
        if (selectedCustomer?.username === value) {
            return;
        }
        setSelectedCustomer(null);
        setQuery(value);
    }, [value, selectedCustomer?.username]);

    useEffect(() => {
        if (selectedCustomer) {
            setSuggestions([]);
            setLoading(false);
            setSearchError("");
            setShowSuggestions(false);
            return undefined;
        }

        const trimmed = query.trim();
        if (trimmed.length < MIN_SEARCH_LENGTH) {
            setSuggestions([]);
            setLoading(false);
            setSearchError("");
            setShowSuggestions(false);
            return undefined;
        }

        setLoading(true);
        setSearchError("");
        const timeoutId = window.setTimeout(async () => {
            try {
                const response = await searchAdminCustomers(trimmed);
                const results = response.data || [];
                setSuggestions(results);
                setShowSuggestions(results.length > 0);
                setActiveIndex(-1);
            } catch (err) {
                setSuggestions([]);
                setShowSuggestions(false);
                setSearchError(err.message);
            } finally {
                setLoading(false);
            }
        }, SEARCH_DEBOUNCE_MS);

        return () => window.clearTimeout(timeoutId);
    }, [query, selectedCustomer]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleInputChange = (event) => {
        const nextValue = event.target.value;
        setSelectedCustomer(null);
        setQuery(nextValue);
        onChange(nextValue);
        setShowSuggestions(true);
    };

    const handleSelectCustomer = (customer) => {
        setSelectedCustomer(customer);
        setQuery("");
        onChange(customer.username);
        onCustomerSelect?.(customer.username);
        setShowSuggestions(false);
        setSuggestions([]);
        setActiveIndex(-1);
    };

    const handleKeyDown = (event) => {
        if (!showSuggestions || suggestions.length === 0) {
            return;
        }

        if (event.key === "ArrowDown") {
            event.preventDefault();
            setActiveIndex((current) => (current + 1) % suggestions.length);
            return;
        }

        if (event.key === "ArrowUp") {
            event.preventDefault();
            setActiveIndex((current) => (current <= 0 ? suggestions.length - 1 : current - 1));
            return;
        }

        if (event.key === "Enter" && activeIndex >= 0) {
            event.preventDefault();
            handleSelectCustomer(suggestions[activeIndex]);
            return;
        }

        if (event.key === "Escape") {
            setShowSuggestions(false);
        }
    };

    return (
        <label className="field customer-filter-field">
            Client
            <div className="customer-filter-combobox" ref={containerRef}>
                <input
                    ref={inputRef}
                    type="search"
                    role="combobox"
                    aria-expanded={showSuggestions}
                    aria-controls={listboxId}
                    aria-autocomplete="list"
                    placeholder="Nom ou identifiant"
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={() => {
                        if (suggestions.length > 0) {
                            setShowSuggestions(true);
                        }
                    }}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    autoComplete="off"
                />
                {loading && <span className="customer-filter-status muted">Recherche...</span>}
                {!loading && !selectedCustomer && query.trim().length > 0 && query.trim().length < MIN_SEARCH_LENGTH && (
                    <span className="customer-filter-status muted">Saisissez au moins 2 caractères</span>
                )}
                {!loading && !selectedCustomer && query.trim().length >= MIN_SEARCH_LENGTH && suggestions.length === 0 && !searchError && (
                    <span className="customer-filter-status muted">Aucun client trouvé</span>
                )}
                {searchError && <span className="customer-filter-status error">{searchError}</span>}
                {showSuggestions && suggestions.length > 0 && (
                    <ul
                        ref={suggestionsRef}
                        id={listboxId}
                        className="customer-filter-suggestions"
                        role="listbox"
                    >
                        {suggestions.map((customer, index) => (
                            <li key={customer.username}>
                                <button
                                    type="button"
                                    role="option"
                                    aria-selected={index === activeIndex}
                                    className={index === activeIndex ? "is-active" : ""}
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => handleSelectCustomer(customer)}
                                >
                                    <strong>{formatCustomerOption(customer)}</strong>
                                    {customer.primaryPhone && (
                                        <span className="muted">{customer.primaryPhone}</span>
                                    )}
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </label>
    );
}
